/**
 * `useSingleRowData` — fetches a single row identified by `{schema, table, pk}`
 * via the shared Table Explorer fetch pipeline. Powers the cross-table
 * Single-Row Details View: when the user drills `customers → #42 → orders →
 * #101`, the underlying TE tab still holds `customers` rows, so the active
 * row-node (`orders #101`) can't be served from `tabContent.rows`. This hook
 * fetches it independently.
 *
 * SQL emit is engine-aware via the underlying `fetchTableExplorerRows` (each
 * engine adapter owns the WHERE-clause dialect). We deliberately do NOT call
 * `buildDrillSql` directly so we inherit the per-engine quoting/coercion
 * behavior that the regular Table Explorer fetch enjoys.
 *
 * Cache key includes `engine` so the same connection-id across engine swaps
 * never reuses a stale row.
 */

import { useQuery } from '@tanstack/react-query';
import { createExecutionId } from '../../../../types/queryExecution';
import { fetchTableExplorerRows } from '../../table-explorer/services/tableExplorerApi';
import type { DatabaseEngineId } from '../../../../core/engines';
import type { FilterCondition, FilterGroup, TableExplorerColumn } from '../../core/types/workspaceTypes';
import type { SingleRowNodeRef } from '../types/singleRowTypes';

export type UseSingleRowDataParams = {
  readonly engine: DatabaseEngineId;
  readonly connectionId: string;
  readonly database: string;
  readonly node: SingleRowNodeRef;
  readonly enabled: boolean;
};

export type UseSingleRowDataResult = {
  readonly row: Record<string, unknown> | null;
  readonly columns: ReadonlyArray<TableExplorerColumn>;
  readonly isLoading: boolean;
  readonly isFetching: boolean;
  readonly error: unknown;
  readonly refetch: () => void;
};

type SingleRowData = {
  readonly row: Record<string, unknown> | null;
  readonly columns: ReadonlyArray<TableExplorerColumn>;
};

function buildPkFilters(
  pk: SingleRowNodeRef['pk'],
): FilterGroup[] {
  return pk.map((p) => ({
    field: p.column,
    conditions: [
      // `FilterCondition['value']` accepts string|number|boolean|Date|null|
      // string[]; PK values are concrete primitives at runtime, and the SQL
      // builder coerces by the column's data type at emit time. We type the
      // PK value as the full `FilterCondition['value']` union so Date/array
      // PK shapes survive (instead of silently narrowing them away).
      { operator: '=', value: p.value as FilterCondition['value'] },
    ],
  }));
}

function hashPk(pk: SingleRowNodeRef['pk']): string {
  return pk.map((p) => `${p.column}=${JSON.stringify(p.value)}`).join('&');
}

export function useSingleRowData(params: UseSingleRowDataParams): UseSingleRowDataResult {
  const { engine, connectionId, database, node, enabled } = params;

  const isRowNode = node?.kind === 'row';
  const hasPk = isRowNode && node.pk.length > 0;

  // Inline `hashPk(node.pk)` directly into the queryKey. A previous
  // `useMemo` depended on `node?.pk` (array reference), so a parent that
  // recreated a structurally-equal `pk` array on every render would mint a
  // new memo result and a new queryKey — defeating the TanStack Query
  // cache. The stringified hash is structurally stable, so React-Query
  // shallow-compares the queryKey tuple correctly across re-mounts.
  const pkHash = isRowNode ? hashPk(node.pk) : '';
  const schema = isRowNode ? node.schema : '';
  const table = isRowNode ? node.table : '';

  const isEnabled = enabled && !!connectionId && !!database && hasPk;

  const query = useQuery<SingleRowData>({
    queryKey: [
      'single-row',
      engine,
      connectionId,
      database,
      schema,
      table,
      pkHash,
    ],
    queryFn: async () => {
      const executionId = createExecutionId(`single-row-${table || 'unknown'}`);
      const filters = buildPkFilters(node.pk);
      const result = await fetchTableExplorerRows({
        connectionId,
        executionId,
        table: { database, schema, tableName: table },
        columns: '*',
        limit: 1,
        filters,
        sorting: [],
      });
      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch single row');
      }
      const firstRow = result.rows && result.rows.length > 0 ? result.rows[0] : null;
      return {
        row: firstRow,
        columns: result.columns || [],
      };
    },
    enabled: isEnabled,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });

  return {
    row: query.data?.row ?? null,
    columns: query.data?.columns ?? [],
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: () => {
      void query.refetch();
    },
  };
}
